import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Row, Col, Skeleton, Badge, Typography } from 'antd';
import {
  faDollarSign,
  faArrowTrendUp,
  faShoppingCart,
  faBullseye,
  faCreditCard,
  faBox,
  faUsers,
  faPercent,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchCustomRangeReport,
} from '../../../redux/CustomRangeReports';
import { useTheme } from '../../../context/ThemeContext';
import {
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryInfo,
  GrossSalesDisplay,
  GrossSalesAmount,
  GrossSalesLabel,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
} from './Styles';
import { addCommasInPrices } from './constants';
import { CustomRangeReportSummaryProps, KeyMetric } from './types';
import { METRIC_IDS, METRIC_COLORS } from './constants';

const { Text } = Typography;

const CustomRangeReportSummary: React.FC<CustomRangeReportSummaryProps> = ({
  merchant,
  storeId,
  selectedDateRange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { reportData, loading } = useSelector(
    (state: RootState) => state.customRangeReports
  );

  const getDateTimestamp = (date: moment.Moment | Date | null): number | null => {
    if (!date) return null;
    if (date instanceof Date) return date.getTime();
    if (moment.isMoment(date)) return date.valueOf();
    return null;
  };

  const startTimestamp = getDateTimestamp(selectedDateRange.startDate);
  const endTimestamp = getDateTimestamp(selectedDateRange.endDate);
  const merchantId = merchant?.id || null;

  const startDateStr = useMemo(() => {
    if (!startTimestamp) return null;
    return moment(startTimestamp).format('YYYY-MM-DD');
  }, [startTimestamp]);

  const endDateStr = useMemo(() => {
    if (!endTimestamp) return null;
    return moment(endTimestamp).format('YYYY-MM-DD');
  }, [endTimestamp]);

  const memoizedMerchantId = useMemo(() => merchantId, [merchantId]);
  
  const previousParamsRef = useRef<string>('');
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!startDateStr || !endDateStr) {
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    const paramsKey = `${startDateStr}-${endDateStr}-${memoizedMerchantId || ''}-${storeId || ''}`;
    
    if (isInitialMountRef.current || previousParamsRef.current !== paramsKey) {
      isInitialMountRef.current = false;
      previousParamsRef.current = paramsKey;
      isFetchingRef.current = true;
      
      const params = {
        startDate: startDateStr,
        endDate: endDateStr,
        merchantId: memoizedMerchantId || undefined,
        storeId: storeId || undefined,
      };
      
      dispatch(fetchCustomRangeReport(params)).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, startDateStr, endDateStr, memoizedMerchantId, storeId]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const salesSummary = reportData?.salesSummary || {
    grossSales: 0,
    netSales: 0,
    orderCount: 0,
    totalRevenue: 0,
    itemsSold: 0,
    tips: 0,
    averageTipRate: 0,
  };

  const keyMetrics: KeyMetric[] = [
    {
      id: METRIC_IDS.GROSS_SALES,
      title: 'Gross Sales',
      subTitle: 'Total sales before deductions',
      value: `$${addCommasInPrices(salesSummary.grossSales || 0)}`,
      icon: faDollarSign,
      color: METRIC_COLORS.GROSS_SALES,
    },
    {
      id: METRIC_IDS.NET_SALES,
      title: 'Net Sales',
      subTitle: 'Sales after deductions',
      value: `$${addCommasInPrices(salesSummary.netSales || 0)}`,
      icon: faArrowTrendUp,
      color: METRIC_COLORS.NET_SALES,
    },
    {
      id: METRIC_IDS.ORDER_COUNT,
      title: 'Total Orders',
      subTitle: 'Number of orders processed',
      value: (salesSummary.orderCount || 0).toString(),
      icon: faShoppingCart,
      color: METRIC_COLORS.ORDER_COUNT,
    },
    {
      id: METRIC_IDS.AVERAGE_ORDER_VALUE,
      title: 'Avg Order Value',
      subTitle: 'Average value per order',
      value:
        salesSummary.orderCount > 0
          ? `$${addCommasInPrices(
              (salesSummary.grossSales || 0) / salesSummary.orderCount
            )}`
          : '$0.00',
      icon: faBullseye,
      color: METRIC_COLORS.AVERAGE_ORDER_VALUE,
    },
    {
      id: METRIC_IDS.TOTAL_REVENUE,
      title: 'Total Revenue',
      subTitle: 'Total revenue generated',
      value: `$${addCommasInPrices(salesSummary.totalRevenue || 0)}`,
      icon: faCreditCard,
      color: METRIC_COLORS.TOTAL_REVENUE,
    },
    {
      id: METRIC_IDS.ITEMS_SOLD,
      title: 'Items Sold',
      subTitle: 'Total items sold',
      value: (salesSummary.itemsSold || 0).toString(),
      icon: faBox,
      color: METRIC_COLORS.ITEMS_SOLD,
    },
    {
      id: METRIC_IDS.TIPS,
      title: 'Total Tips',
      subTitle: 'Tips received',
      value: `$${addCommasInPrices(salesSummary.tips || 0)}`,
      icon: faUsers,
      color: METRIC_COLORS.TIPS,
    },
    {
      id: METRIC_IDS.AVERAGE_TIP_RATE,
      title: 'Avg Tip Rate',
      subTitle: 'Average tip percentage',
      value: `${(salesSummary.averageTipRate || 0).toFixed(2)}%`,
      icon: faPercent,
      color: METRIC_COLORS.AVERAGE_TIP_RATE,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <SummaryCard>
        <SummaryHeader>
          <SummaryTitle>Custom Range Report Summary</SummaryTitle>
          <SummaryInfo>
            {loading ? (
              <Skeleton.Input active size="small" style={{ width: 150 }} />
            ) : (
              <>
                <Text type="secondary">
                  Merchant: {reportData?.merchantName || 'N/A'}
                </Text>
                <Badge
                  color="blue"
                  text={reportData?.reportType || 'custom_range'}
                />
              </>
            )}
          </SummaryInfo>
        </SummaryHeader>

        {loading ? (
          <Skeleton.Input active style={{ width: 300, marginBottom: 16 }} />
        ) : (
          reportData?.period?.startDate && reportData?.period?.endDate && (
            <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
              {moment(reportData.period.startDate).format('MMM DD, YYYY')} -{' '}
              {moment(reportData.period.endDate).format('MMM DD, YYYY')}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({reportData.period.totalDays || 0} days)
              </Text>
            </Text>
          )
        )}

        <GrossSalesDisplay>
          {loading ? (
            <Skeleton.Input active size="large" style={{ width: 200, height: 48 }} />
          ) : (
            <GrossSalesAmount>
              ${addCommasInPrices(salesSummary.grossSales || 0)}
            </GrossSalesAmount>
          )}
          <GrossSalesLabel>
            <FontAwesomeIcon icon={faArrowTrendUp} />
            <span>Gross Sales</span>
          </GrossSalesLabel>
        </GrossSalesDisplay>

        <div>
          {loading ? (
            <Row gutter={[16, 16]}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Col>
              ))}
            </Row>
          ) : (
            <>
              <Typography.Title
                level={4}
                style={{ marginBottom: 16, fontWeight: 700 }}
              >
                Key Metrics
              </Typography.Title>
              <MetricsGrid gutter={[16, 16]}>
                {keyMetrics.map((metric) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={metric.id}>
                    <MetricCard>
                      <MetricHeader>
                        <MetricIcon color={metric.color}>
                          <FontAwesomeIcon icon={metric.icon} />
                        </MetricIcon>
                        <MetricTitle>{metric.title}</MetricTitle>
                      </MetricHeader>
                      <MetricValue>{metric.value}</MetricValue>
                    </MetricCard>
                  </Col>
                ))}
              </MetricsGrid>
            </>
          )}
        </div>
      </SummaryCard>
    </ThemeProvider>
  );
};

export default CustomRangeReportSummary;
